/* ═══════════════════════════════════════════════════════════════
   Project placeholders — `{P1}`, `{P2}`, … and `{IssuanceDate}`.

   Josh's model (2026-08-05): a client's formatting is ~99% identical
   across that client's projects; only the header/footer CONTENT
   varies. So a MASTER forma carries numbered blanks, and the project
   copy says what each blank means.

   This module is the ONE brain for that: which tokens are
   placeholders, what they resolve to, and which are still unbound.
   Pure logic — no I/O, no React, no OOXML — so the importer, the
   canvas, the binding GUI, and the Word engine all agree.
   ═══════════════════════════════════════════════════════════════ */

import type { FormaBinding, StandardConfig } from './forma';

/** The token that renders the selected issuance's date. */
export const ISSUANCE_DATE_TOKEN = '{IssuanceDate}';
/** Its id inside `placeholders` / `bindings` (token minus the braces). */
export const ISSUANCE_DATE_ID = 'IssuanceDate';

const P_TOKEN = /^\{P(\d+)\}$/;

/** "{P4}" → "P4"; "{IssuanceDate}" → "IssuanceDate"; anything else → null.
 *  Pass the token BASE (mods already stripped). */
export function placeholderId(base: string): string | null {
  if (base === ISSUANCE_DATE_TOKEN) return ISSUANCE_DATE_ID;
  return P_TOKEN.test(base) ? base.slice(1, -1) : null;
}

/** True for `{P#}` only — `{IssuanceDate}` is a placeholder too, but a
 *  numbered one is what the palette mints and the importer counts. */
export function isNumberedPlaceholder(base: string): boolean {
  return P_TOKEN.test(base);
}

/** Strip a token's trailing `|mods` the same way the rest of the app does.
 *  Kept local so this module has no dependency on renderer utils. */
function baseOf(token: string): string {
  const pipe = token.lastIndexOf('|');
  if (pipe <= 0) return token;
  return token.slice(0, pipe) + (token.endsWith('}') ? '}' : '');
}

/** Every zone array in a forma, in document order (header → body → footer). */
export function formaZones(config: StandardConfig): string[][] {
  const hf: any = config.headerFooter || {};
  const sp: any = config.special || {};
  const keys = [
    'headerFirstLeft',
    'headerFirstCenter',
    'headerFirstRight',
    'headerOddLeft',
    'headerOddCenter',
    'headerOddRight',
    'headerEvenLeft',
    'headerEvenCenter',
    'headerEvenRight',
    'footerFirstLeft',
    'footerFirstCenter',
    'footerFirstRight',
    'footerOddLeft',
    'footerOddCenter',
    'footerOddRight',
    'footerEvenLeft',
    'footerEvenCenter',
    'footerEvenRight',
  ];
  const zones: string[][] = keys.map((k) => (Array.isArray(hf[k]) ? hf[k] : []));
  for (const k of [
    'bodyTopLeft',
    'bodyTopCenter',
    'bodyTopRight',
    'postLeft',
    'postCenter',
    'postRight',
  ])
    if (Array.isArray(sp[k])) zones.push(sp[k]);
  return zones;
}

/** Placeholder ids actually PLACED in this forma, numeric order first
 *  ("P1", "P2", "P10") then `IssuanceDate`. Drives the binding GUI's rows
 *  and the export gate — a placeholder that was deleted from the canvas
 *  never blocks an export. */
export function usedPlaceholderIds(config: StandardConfig): string[] {
  const seen = new Set<string>();
  for (const zone of formaZones(config)) {
    for (const token of zone) {
      const id = placeholderId(baseOf(token));
      if (id) seen.add(id);
    }
  }
  const nums = [...seen]
    .filter((id) => id !== ISSUANCE_DATE_ID)
    .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
  return seen.has(ISSUANCE_DATE_ID) ? [...nums, ISSUANCE_DATE_ID] : nums;
}

/** The next free `{P#}` for this forma — what the palette chip mints.
 *  Counts placed tokens AND remembered ones, so re-adding never collides
 *  with a memory that is still on file. */
export function nextPlaceholderToken(config: StandardConfig): string {
  let max = 0;
  const bump = (id: string) => {
    if (id !== ISSUANCE_DATE_ID) max = Math.max(max, Number(id.slice(1)) || 0);
  };
  usedPlaceholderIds(config).forEach(bump);
  Object.keys(config.placeholders || {}).forEach(bump);
  return `{P${max + 1}}`;
}

// ─── date formatting (the forma's own dateFormat, not the OS locale) ───

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Format a date with the Word-style picture strings the Date Format picker
 *  offers (M/d/yyyy, MMMM d, yyyy, yyyy-MM-dd…). Longest tokens first so
 *  "MMMM" never gets eaten as "MM" + "MM". Unparseable input → "". */
export function formatFormaDate(value: string | Date | undefined, pattern?: string): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(String(value).replace(/-/g, '/'));
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const map: Record<string, string> = {
    MMMM: MONTHS[d.getMonth()],
    MMM: MONTHS[d.getMonth()].slice(0, 3),
    MM: pad(d.getMonth() + 1),
    M: String(d.getMonth() + 1),
    dddd: d.toLocaleDateString('en-US', { weekday: 'long' }),
    dd: pad(d.getDate()),
    d: String(d.getDate()),
    yyyy: String(d.getFullYear()),
    yy: String(d.getFullYear()).slice(-2),
  };
  return (pattern || 'M/d/yyyy').replace(/MMMM|MMM|MM|M|dddd|dd|d|yyyy|yy/g, (m) => map[m]);
}

// ─── resolution ───

export interface PlaceholderContext {
  /** Resolved project/client/issuance values keyed like the token minus its
   *  braces — "Project.Name", "Client.Name", "Issuance.Name"… (the same map
   *  the word engine already receives as docMeta.project). */
  project?: Record<string, string>;
  /** This spec's issuance history, newest last. */
  issuances?: Array<{ name?: string; date?: string; issuedDate?: string }>;
  /** The forma's date format, e.g. "MMMM d, yyyy". */
  dateFormat?: string;
}

function pickIssuance(ctx: PlaceholderContext, name: string) {
  const list = ctx.issuances || [];
  if (!list.length) return null;
  if (!name) return list[list.length - 1];
  const hit = list.find((i) => (i.name || '').trim().toLowerCase() === name.trim().toLowerCase());
  return hit || list[list.length - 1];
}

/** One binding → the text it prints. Empty string means "nothing to print". */
export function resolveBinding(binding: FormaBinding | undefined, ctx: PlaceholderContext): string {
  if (!binding) return '';
  if (binding.kind === 'text') return binding.value || '';
  if (binding.kind === 'field') {
    const key = binding.value.replace(/^\{|\}$/g, '');
    return (ctx.project && ctx.project[key]) || '';
  }
  const iss = pickIssuance(ctx, binding.value || '');
  return iss?.name || '';
}

/** Every placeholder this forma uses → the text it prints in THIS project.
 *  Keys are ids ("P1", "IssuanceDate"), ready to merge into the engine's
 *  projectMeta map. Unbound placeholders are simply absent. */
export function resolvePlaceholders(
  config: StandardConfig,
  ctx: PlaceholderContext
): Record<string, string> {
  const out: Record<string, string> = {};
  const bindings = config.bindings || {};
  for (const id of usedPlaceholderIds(config)) {
    const b = bindings[id];
    if (!b) continue;
    if (id === ISSUANCE_DATE_ID) {
      // The issuance picker chooses WHICH issuance; the forma's date format
      // decides how its date is written.
      const iss = pickIssuance(ctx, b.kind === 'issuance' ? b.value || '' : '');
      const raw =
        b.kind === 'text'
          ? b.value
          : b.kind === 'field'
            ? (ctx.project && ctx.project[b.value.replace(/^\{|\}$/g, '')]) || ''
            : iss?.date || iss?.issuedDate || '';
      const formatted = formatFormaDate(raw, ctx.dateFormat);
      if (formatted) out[id] = formatted;
      else if (b.kind === 'text' && b.value) out[id] = b.value;
      continue;
    }
    const v = resolveBinding(b, ctx);
    if (v) out[id] = v;
  }
  return out;
}

/** Placeholders that would print nothing — the export gate's blocklist.
 *  A binding that exists but resolves to empty (issuance with no date, a
 *  project field nobody filled in) counts as unbound: shipping a blank where
 *  the client expects their project name is the same failure as shipping
 *  "{P4}". */
export function unboundPlaceholders(config: StandardConfig, ctx: PlaceholderContext): string[] {
  const resolved = resolvePlaceholders(config, ctx);
  return usedPlaceholderIds(config).filter((id) => !resolved[id]);
}

/** Human label for a placeholder id — "P4", "Issuance Date". */
export function placeholderLabel(id: string): string {
  return id === ISSUANCE_DATE_ID ? 'Issuance Date' : id;
}
